/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { OktaAuth } from '@okta/okta-auth-js';
import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { setUser as sentrySetUser } from '@sentry/browser';

import { RootState } from '../../../app/store';
import pearlApi, { clearAuthToken, fetchAuthOkta, setAuthToken } from '../api/pearlApi';
import { clearDiagnosticReportsCache } from '../Fhir/fhir_network';
import { analyticsInitialize, analyticsTrack } from '../analytics_utils';
import { deleteSmartFhirCookieData, SmartFhirCookieType } from '../Epic/smartFhirUtils';
import { setPatientContext } from '../Fhir/fhirSlice';
import { snackNotifications } from '../notification/SnackBarUtils';
// eslint-disable-next-line import/no-restricted-paths
import { deleteAllMyDocumentVersionLocksThunk } from '../../Doc/creatorChatSlice';
import { deleteShareVueCookieData, ShareVueCookieType } from '../ShareVue/ShareVueUtils';

/* User from POST /auth/okta endpoint

   Example response:

   {
    "data": {
        "accessToken": "eyJhbGciOiJSUzI1NiJ9...so9O-Md0V3KhiNQ",
        "givenName": "Craig",
        "initials": "CE",
        "name": "Craig Eytor",
        "orgCode": "PROV",
        "orgId": "a771587a-5e91-4b36-acfe-c998738ed067",
        "orgName": "Providence",
        "orgRole": "CREATOR",
        "title": "Doctor",
        "userId": "d0c5107c-aeac-4469-a8ad-8cbe3c86379c"
    }
   }
*/

export type OrgRoleType = 'ORG_ADMIN' | 'CREATOR' | 'CONSUMER' | 'PUBLISHER' | 'MED_PEARL_ONE';

export function isOrgCreator(role?: OrgRoleType): boolean {
  if (!role) return false;
  return role === 'CREATOR' || role === 'ORG_ADMIN' || role === 'PUBLISHER';
}

export type UserFlagsType = {
  edit_contextualization?: boolean;
  custom_MRN_in_creator?: boolean;
  custom_MRN_in_consumer?: boolean;
  edit_predefined_widgets?: boolean;
  patient_notes_summary?: boolean; // whether to show the patient notes summary button
  allowed_custom_MRNs?: string[] | string;
  edit_published_docs?: boolean; // Can edit published documents belonging to the same org
  show_banner?: boolean;
  'document-ai-chat-feature'?: boolean;
  can_publish?: boolean;
  can_archive?: boolean;
  can_edit_document_flags?: boolean;
  /// Whether the user has an option in the document version More Menu to remove all <fhir> tags in a document version
  can_remove_contextualization?: boolean;
  trusana?: boolean;
  albertsons?: boolean;
  show_analytics_counts?: boolean; // Whether to show the search counts at the top of the chat
  consumer_medication_search?: boolean; // Whether UI to show drug details on search
  'consumer-search-preview'?: boolean;
  pilot_feedback_form?: boolean; // An ORG level feature flag to decide which feedback form a user should see
  view_cme?: boolean; //An ORG level feature flag to show CME(currently for PROV and MUSC users).
  'show-patient-medication'?: boolean; // Whether to show the current patient medication above the chat
  show_npi?: boolean; // Whether to show or hide the NPI message for login user;
  show_contextualization?: boolean; // Whether to show the contextualization button in the chat
  ner_contextualization_expansion?: boolean; // Whether to show the NER contextualization suggestions
  'show-patient-problem-list-past-medical-history'?: boolean; // Whether to show the problem list & past medical hx fhir panels
  creator_expanded_search_summary?: boolean;
  creator_algolia_search?: boolean; //whether to show algolia search in knowledge hub of creator
  dynamed_tab?: boolean; // Whether to show the Dynamed tab in guide tabs
  'musc-contextualization'?: boolean; // Whether to use the muscRules in contextualization
  'admin-download-patient'?: boolean; // Whether to show the admin debug panel for downloading (anonymized) patient data
  'use-open-ai-for-contextualization'?: boolean; // Whether to offer a button to use OpenAI for contextualization
  'super-admin-ui'?: boolean; //Whether to enable access to the user for affiliateAdmin dashboard
  'manage-enrollments'?: boolean; // Whether the user can manage enrollment users for their organization
  CareEverywhere?: boolean; // Whether to show the Care Everywhere data
  enable_epic_embedded_view?: boolean; // Whether to show the Epic embedded view (medpearl in epic)
};
/** This must match what POST /auth/okta returns */
export interface PearlUser {
  accessToken?: string;
  givenName?: string;
  familyName?: string;
  initials?: string;
  name?: string;
  userId: string;
  creator: boolean; // We calculate this, based on orgRole
  orgName?: string;
  orgCode?: 'PROV' | 'ALB' | 'DEMO' | 'MUSC' | 'PAC';
  orgId?: string;
  orgRole: OrgRoleType;
  principalName?: string; // User Principal Name (UPN) in Active Directory/Okta.  This is the email address of the user as used in LaunchDarkly etc, e.g., jdoe@example.com.
  samAccountName?: string; // Security Account Manager Account Name, which is the legacy Windows/AD login name. It's usually shorter and without the domain, e.g., jdoe.
  title?: string;
  email?: string;
  userFlags?: UserFlagsType;
  sessionId?: string;
  sourceApp?: string;
  readableLibraries?: IPearlLibrary[];
  writableLibraries?: IPearlLibrary[];
  defaultLibraryId: string;
  userCreatedAt: string;
}

export interface IPearlLibrary {
  id: string;
  name?: string;
}

export interface LoginErrorResponse {
  title: string;
  detail: string;
}

export interface Login {
  errors?: LoginErrorResponse[];
  errorModalOpen: boolean;
  fromSmartFhirCookie: boolean;
  fromShareVueCookie: boolean;
}
export interface SurveysAccepted {
  name: string;
  id?: string;
  last_accepted_at?: string;
  show?: boolean;
}
export interface NPSQuestions {
  id: string;
  question_type: string;
  title: string;
  position: number;
  created_at?: string;
  description?: string;
  survey_id: string;
  updated_at?: string;
}
export interface NPSSurvey {
  id: string;
  name: string;
  questions: NPSQuestions[];
  description?: string;
  starts_at?: string;
  updated_at?: string;
  created_at?: string;
}

export type OrgViewType = 'Full View' | 'MUSC View' | 'Providence View';

export interface UserState {
  pearlUserInfo?: PearlUser;
  login: Login;
  fromSmartFhirCookie?: boolean;
  fromShareVueCookie?: boolean;
  corsErrorModalOpen: boolean;
  isLoggingOut: boolean; // Whether the user has pressed the logout button.  We don't want to acquire document locks (for example)
  surveysAccepted?: SurveysAccepted[];
  npsSurvey?: NPSSurvey;
  orgView?: OrgViewType; // Only used for creator mode, whether to show the full view (default)
}

const initialState: UserState = {
  pearlUserInfo: undefined,
  isLoggingOut: false,
  login: { errors: undefined, errorModalOpen: false, fromSmartFhirCookie: false, fromShareVueCookie: false },
  corsErrorModalOpen: false,
};

/* On firefox, cors gives the error "TypeError: NetworkError when attempting to fetch resource."
   and on Chrome it is "TypeError: Failed to fetch. */
const isCorsError = (err: any) => err.name === 'AuthApiError' && !err.errorCode && err.xhr.name === 'TypeError';

export const doLogin = createAsyncThunk(
  'user/login',
  async ({ oktaAuth, forceRelogin }: { oktaAuth: OktaAuth | undefined; forceRelogin: boolean }) => {
    try {
      analyticsTrack('login_initiated', {}, undefined);
      await oktaAuth?.signInWithRedirect(forceRelogin ? { prompt: 'login' } : undefined);
      // Note: When this sucessfully logs in, oktaAuthStateChanged will be called below,
      // which then logs in with our own backend.
      return { success: true };
    } catch (err: any) {
      console.log('doLogin error:', err.name, err.errorCode, err.xhr?.message);
      return { success: false };
    }
  }
);

export const doLogout = createAsyncThunk<
  { success: true } | { success: false; corsError: boolean },
  OktaAuth | undefined,
  { state: RootState }
>('user/logout', async (oktaAuth, thunkAPI) => {
  clearDiagnosticReportsCache();
  deleteSmartFhirCookieData();
  deleteShareVueCookieData();
  if (deleteAllMyDocumentVersionLocksThunk) {
    // tslint:disable-next-line
    await thunkAPI.dispatch(deleteAllMyDocumentVersionLocksThunk()).unwrap();
  }
  clearAuthToken();
  const redirectKey = sessionStorage.getItem('logoutRedirect.key') || '';
  const basename = window.location.origin + '/' + redirectKey;
  try {
    if (oktaAuth) {
      await oktaAuth?.signOut({ postLogoutRedirectUri: basename });
    } else {
      // Redirect back to /
      window.location.href = basename;
    }
    // The value we return becomes the `fulfilled` action payload
    return { success: true };
  } catch (err: any) {
    if (isCorsError(err)) {
      // The value we return becomes the `fulfilled` action payload
      return { success: false, corsError: true };
    } else {
      console.log('doLogout error', err?.name, err?.errorCode, err?.xhr?.message);
      throw err;
    }
  }
});

export const smartFhirCookieLoginThunk = createAsyncThunk<
  string | undefined, // return type
  { smartFhirCookieData: Partial<SmartFhirCookieType> | undefined },
  { state: RootState }
>('user/smartFhirCookieLogin', async ({ smartFhirCookieData }, thunkAPI) => {
  if (!smartFhirCookieData || smartFhirCookieData.error) {
    // No smart-fhir cookie, so if we think we are logged in via cookie, logout!
    if (thunkAPI.getState().user.fromSmartFhirCookie) {
      clearAuthToken();
      thunkAPI.dispatch(setPearlUserInfo(undefined));
    }
    if (smartFhirCookieData?.error) {
      const asJson = JSON.stringify(smartFhirCookieData);
      console.error('smartFhirCookieLoginThunk:', asJson);
      snackNotifications.error('SMART Fhir: ' + smartFhirCookieData.error);
      thunkAPI.dispatch(
        openLoginErrorModal({
          errors: [{ title: smartFhirCookieData.error, detail: asJson }],
          errorModalOpen: true,
          fromSmartFhirCookie: true,
          fromShareVueCookie: false,
        })
      );
      // We could delete the cookie, but then the user would be redirected to the login page and not
      // see the snackNotification. So we leave the cookie in place, and the user will see the
      // error page.
      //deleteSmartFhirCookieData();
    }
    // Not Logged in via cookie, nothing more to do.  (Note we might be logged in via
    // okta at this stage
    return;
  }

  if (smartFhirCookieData.type === 'NEW_COOKIE') {
    // Here we must make another call to get the full information
    const resp = await pearlApi.post('/auth/smart-fhir', smartFhirCookieData.id);
    smartFhirCookieData = {
      ...(resp?.data?.data || resp.data),
      type: 'OLD_COOKIE',
    };
  }
  if (!smartFhirCookieData || smartFhirCookieData.type !== 'OLD_COOKIE') {
    console.error('internal error converting cookie - should never happen', smartFhirCookieData);
    return;
  }

  const fhirPatientId = smartFhirCookieData.context?.patient_id;
  if (fhirPatientId) {
    thunkAPI.dispatch(
      setPatientContext({
        fhirPatientId,
        server: smartFhirCookieData.context?.server_name,
        fromSmartOnFhir: true,
      })
    );
  }
  let pearlUserInfo = smartFhirCookieData.auth as PearlUser | undefined;
  if (pearlUserInfo) {
    setAuthToken(pearlUserInfo.accessToken);
    sentrySetUser({ id: pearlUserInfo?.userId, email: pearlUserInfo?.email });
    analyticsInitialize(pearlUserInfo);
    if (!pearlUserInfo.userFlags) {
      // fetch the flags
      pearlUserInfo = { ...pearlUserInfo, userFlags: await fetchFeatureFlags() };
    }
    thunkAPI.dispatch(setPearlUserInfo({ pearlUserInfo, fromSmartFhirCookie: true, fromShareVueCookie: false }));
  }
  return smartFhirCookieData.context?.consumer_path;
});

export const shareVueCookieLoginThunk = createAsyncThunk<
  void, // return type
  { shareVueCookieData: Partial<ShareVueCookieType> | undefined },
  { state: RootState }
>('user/shareVueCookieLogin', async ({ shareVueCookieData }, thunkAPI) => {
  if (!shareVueCookieData || shareVueCookieData.error) {
    // No share-vue cookie, so if we think we are logged in via cookie, logout!
    if (thunkAPI.getState().user.login.fromShareVueCookie) {
      clearAuthToken();
      thunkAPI.dispatch(setPearlUserInfo(undefined));
    }
    if (shareVueCookieData?.error) {
      const asJson = JSON.stringify(shareVueCookieData);
      console.error('shareVueCookieLoginThunk:', asJson);
      snackNotifications.error('Share Vue: ' + shareVueCookieData.error);
      thunkAPI.dispatch(
        openLoginErrorModal({
          errors: [{ title: shareVueCookieData.error, detail: asJson }],
          errorModalOpen: true,
          fromShareVueCookie: true,
          fromSmartFhirCookie: false,
        })
      );
    }
    return;
  }
  if (!shareVueCookieData) {
    console.error('internal error converting cookie - should never happen', shareVueCookieData);
    return;
  }

  let pearlUserInfo = shareVueCookieData.auth as PearlUser | undefined;
  if (pearlUserInfo) {
    setAuthToken(pearlUserInfo.accessToken);
    sentrySetUser({ id: pearlUserInfo?.userId, email: pearlUserInfo?.email });
    analyticsInitialize(pearlUserInfo);
    if (!pearlUserInfo.userFlags) {
      // fetch the flags
      pearlUserInfo = { ...pearlUserInfo, userFlags: await fetchFeatureFlags() };
    }
    thunkAPI.dispatch(setPearlUserInfo({ pearlUserInfo, fromShareVueCookie: true, fromSmartFhirCookie: false }));
  }
  return;
});

/** Fetch from:  GET /featureFlags
 *  Response is:
 *  {
 *    data: {
 *      can_publish: false,
 *      edit_contextualization: false,
 *    }
 *  }
 */
async function fetchFeatureFlags(): Promise<UserFlagsType> {
  const flags = await pearlApi.get('/featureFlags');
  return flags.data.data as UserFlagsType;
}

export const oktaAuthStateChanged = createAsyncThunk<
  void,
  {
    loggedout: boolean;
    pearlUserInfo?: PearlUser;
    overrideRole?: OrgRoleType;
  },
  { state: RootState }
>('user/oktaAuthStateChanged', async ({ loggedout, pearlUserInfo, overrideRole }, thunkAPI) => {
  if (loggedout) {
    if (thunkAPI.getState().user.fromSmartFhirCookie || thunkAPI.getState().user.fromShareVueCookie) {
      // We were logged in via smart-fhir cookie, so don't logout
      return;
    } else {
      // When user isn't authenticated, forget any user info
      clearAuthToken();
      sessionStorage.setItem('devWarning.show', 'true');
      thunkAPI.dispatch(setPearlUserInfo(undefined));
    }
  } else if (!pearlUserInfo) {
    try {
      await fetchAuthOkta()
        .then(async (user) => {
          setAuthToken(user.data.data.accessToken);
          const pearlUserInfo = user.data.data as PearlUser;
          if (overrideRole && pearlUserInfo.orgRole === 'ORG_ADMIN')
            pearlUserInfo.orgRole = overrideRole.toUpperCase() as OrgRoleType;
          thunkAPI.dispatch(setPearlUserInfo({ pearlUserInfo, fromSmartFhirCookie: false, fromShareVueCookie: false }));
          sentrySetUser({ id: pearlUserInfo?.userId, email: pearlUserInfo?.email });
          analyticsInitialize(pearlUserInfo);
        })
        .catch((err) => {
          console.log('oktaAuthStateChanged: not logged in', err?.message || err?.name);
          const errors = [{ title: err.message ?? err.name ?? err.title ?? 'Unknown Error', detail: '' }];
          if (err.response?.data?.errors) {
            errors.push(...err.response?.data?.errors?.map((e: any) => ({ title: e.title, detail: e.detail })));
          }
          thunkAPI.dispatch(
            openLoginErrorModal({
              errors,
              errorModalOpen: true,
              fromSmartFhirCookie: false,
              fromShareVueCookie: false,
            })
          );
          clearAuthToken();
        });
    } catch (err) {
      console.error('caught!', err);
    }
  } else if (overrideRole) {
    if (pearlUserInfo.orgRole === 'ORG_ADMIN') pearlUserInfo.orgRole = overrideRole.toUpperCase() as 'ORG_ADMIN';
    thunkAPI.dispatch(setPearlUserInfo({ pearlUserInfo, fromSmartFhirCookie: false, fromShareVueCookie: false }));
  }
});
export const fetchSurveyInfo = createAsyncThunk<SurveysAccepted[]>('user/fetchSurveyInfo', async () => {
  try {
    const {
      data: { data },
    } = await pearlApi.get(`/prompts`);
    console.log('data from survey in thunk', data);
    return data;
  } catch (err) {
    snackNotifications.error(`Failed to fetch survey info: ${(err as Error).message}`);
    throw err;
  }
});
export const fetchNpsSurveyInfo = createAsyncThunk<NPSSurvey>('user/fetchNpsSurveyInfo', async () => {
  try {
    const response = await pearlApi.get<{ data: NPSSurvey }>('/surveys/nps');
    let survey = response.data.data;
    if (survey.questions) {
      survey = {
        ...survey,
        questions: [...survey.questions].sort((a, b) => a.position - b.position),
      };
    }
    return survey;
  } catch (err) {
    snackNotifications.error(`Failed to NPS survey info: ${(err as Error).message}`);
    throw err;
  }
});

export const userSlice = createSlice({
  name: 'user',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setPearlUserInfo: (
      state,
      action: PayloadAction<
        | {
            pearlUserInfo: PearlUser;
            fromSmartFhirCookie: boolean | undefined;
            fromShareVueCookie: boolean | undefined;
          }
        | undefined
      >
    ) => {
      state.pearlUserInfo = action.payload?.pearlUserInfo;
      state.fromSmartFhirCookie = action.payload?.fromSmartFhirCookie;
      state.fromShareVueCookie = action.payload?.fromShareVueCookie;
    },
    setOrgView: (state, action: PayloadAction<OrgViewType>) => {
      state.orgView = action.payload;
    },
    closeCorsErrorModal: (state) => {
      // User saw the cors error modal and closed it
      state.corsErrorModalOpen = false;
    },
    closeLoginErrorModal: (state) => {
      // User saw login error modal and closed it
      state.login.errorModalOpen = false;
    },
    openLoginErrorModal: (state, action: PayloadAction<Login>) => {
      // Show login error modal
      state.login.errors = action.payload?.errors;
      state.login.errorModalOpen = action.payload?.errorModalOpen || false;
      state.login.fromSmartFhirCookie = action.payload?.fromSmartFhirCookie || false;
      state.login.fromShareVueCookie = action.payload?.fromShareVueCookie || false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(doLogout.fulfilled, (state, action) => {
        if (!action.payload.success && action.payload.corsError) {
          // Failed because of CORS
          state.corsErrorModalOpen = true;
        }
      })
      .addCase(doLogout.pending, (state) => {
        // Keep track of that we are trying to logout.  useDocumentLockManager uses this to know not to try to relock the document
        state.isLoggingOut = true;
      })
      .addCase(doLogout.rejected, (state) => {
        state.isLoggingOut = false;
      })
      .addCase(doLogin.pending, (state) => {
        state.isLoggingOut = false;
      })
      .addCase(doLogin.rejected, (state) => {
        state.login.errorModalOpen = true;
        state.isLoggingOut = false;
      })
      .addCase(doLogin.fulfilled, (state) => {
        state.isLoggingOut = false;
      })
      .addCase(fetchSurveyInfo.fulfilled, (state, action) => {
        state.surveysAccepted = action.payload;
      })
      .addCase(fetchNpsSurveyInfo.fulfilled, (state, action) => {
        state.npsSurvey = action.payload;
      });
  },
});

export const { setPearlUserInfo, closeCorsErrorModal, closeLoginErrorModal, openLoginErrorModal, setOrgView } =
  userSlice.actions;

export const selectPearlUserInfo = (state: RootState): PearlUser | undefined => state.user.pearlUserInfo;
export const selectOrgView = (state: RootState): OrgViewType | undefined => state.user.orgView;
export const selectLogin = (state: RootState): Login => state.user.login;
export const selectCorsErrorModalOpen = (state: RootState): boolean => state.user.corsErrorModalOpen;
export const selectIsLoggingOut = (state: RootState): boolean => state.user.isLoggingOut;
export const selectUserFlags = (state: RootState): UserFlagsType | undefined => state.user.pearlUserInfo?.userFlags;
export const selectUserSurveyInfo = (state: RootState): SurveysAccepted[] | undefined => state.user.surveysAccepted;
export const selectSearchSourceType = (state: RootState): string => (!state.chat.creatorMode ? 'algolia' : 'pg');
export const selectCanViewSearchPreview = (state: RootState): boolean | undefined =>
  !state.chat.creatorMode && state.user.pearlUserInfo?.userFlags?.['consumer-search-preview'];
export const selectShouldUserViewPilotFeedbackForm = (state: RootState): boolean | undefined =>
  state.user.pearlUserInfo?.userFlags?.pilot_feedback_form;
export const selectCanViewCME = (state: RootState): boolean | undefined =>
  state.user.pearlUserInfo?.userFlags?.view_cme;

export const selectNPSSurvey = (state: RootState) => state.user.npsSurvey;
export const selectCanUserViewNPS = (state: RootState): boolean => {
  const userCreatedAt = state.user.pearlUserInfo?.userCreatedAt;
  if (!userCreatedAt) {
    return false;
  }
  const createdAtDate = new Date(userCreatedAt);
  const currentDate = new Date();
  // Ensure we only have valid dates
  if (isNaN(createdAtDate.getTime())) {
    return false;
  }
  const differenceInTime = currentDate.getTime() - createdAtDate.getTime();
  const differenceInDays = differenceInTime / (1000 * 3600 * 24);
  return differenceInDays > 15;
};
const selectAllowedCustomMRNsRaw = (state: RootState): string[] | string | undefined =>
  state.user.pearlUserInfo?.userFlags?.allowed_custom_MRNs;

export const selectLoadCustomMRNsFromAzure = createSelector(
  [selectAllowedCustomMRNsRaw],
  (allowed_custom_MRNs) => allowed_custom_MRNs === 'test'
);

export const selectIsLoggedInThroughSmartFhirCookie = (state: RootState): boolean => !!state.user.fromSmartFhirCookie;
export const selectIsLoggedInThroughShareVueCookie = (state: RootState): boolean => !!state.user.fromShareVueCookie;
export const selectIsMuscConsumer = (state: RootState): boolean =>
  !state.chat.creatorMode && state.user.pearlUserInfo?.orgCode === 'MUSC';

export default userSlice.reducer;
